import { ASSET_CLASS_IDS } from "~/consts";
import type { MultiSelectOption } from "~/components/MultiSelect";

// // // //

export type AssetClassGroupDefinition = {
    id: string;
    label: string;
    memberIds: number[];
};

/**
 * Coarse asset class groupings used by the asset screener v2 filter.
 */
export const ASSET_CLASS_GROUP_DEFINITIONS: AssetClassGroupDefinition[] = [
    {
        id: "equities",
        label: "All Equities",
        memberIds: [
            ASSET_CLASS_IDS.Stocks,
            ASSET_CLASS_IDS.PrivateEquity,
            ASSET_CLASS_IDS.VentureCapital,
        ],
    },
    {
        id: "alternatives",
        label: "All Alternatives",
        memberIds: [ASSET_CLASS_IDS.ActiveStrategies],
    },
    {
        id: "credit",
        label: "All Credit",
        memberIds: [
            ASSET_CLASS_IDS.CorporateCredit,
            ASSET_CLASS_IDS.DiversifiedCredit,
            ASSET_CLASS_IDS.AssetBackedCredit,
            ASSET_CLASS_IDS.SpecialtyFinance,
            ASSET_CLASS_IDS.PrivateCredit,
            ASSET_CLASS_IDS.MunicipalBonds,
            ASSET_CLASS_IDS.RepurchaseAgreements,
        ],
    },
    {
        id: "rates",
        label: "All Rates",
        memberIds: [
            ASSET_CLASS_IDS.TreasuryProduct,
            ASSET_CLASS_IDS.NonUSGovernmentDebt,
        ],
    },
    {
        id: "commodities",
        label: "All Commodities",
        memberIds: [ASSET_CLASS_IDS.Commodities, ASSET_CLASS_IDS.RealEstate],
    },
];

export type AssetClassGroup = {
    id: string;
    label: string;
    members: MultiSelectOption[];
};

/**
 * Builds renderable asset class groups from the available filter options.
 */
export function buildAssetClassGroups(
    assetClassOptions: MultiSelectOption[],
): AssetClassGroup[] {
    const optionsByValue = new Map(
        assetClassOptions.map((option) => [option.value, option]),
    );
    const groupedOptionValues = new Set<string>();

    const groups = ASSET_CLASS_GROUP_DEFINITIONS.map((group) => {
        const members = group.memberIds
            .map((memberId) => optionsByValue.get(String(memberId)))
            .filter((option): option is MultiSelectOption => option != null);

        members.forEach((member) => groupedOptionValues.add(member.value));

        return {
            id: group.id,
            label: group.label,
            members,
        };
    }).filter((group) => group.members.length > 0);

    const ungroupedMembers = assetClassOptions.filter(
        (option) => !groupedOptionValues.has(option.value),
    );

    if (ungroupedMembers.length > 0) {
        groups.push({
            id: "other",
            label: "All Other",
            members: ungroupedMembers,
        });
    }

    return groups;
}
